import { useState } from 'react';
import { Plus, Search, Pencil, Building2, ClipboardList } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Spinner, EmptyState } from '@/shared/ui/Spinner';
import { useCustomers } from './customers.api';
import { CustomerFormDialog } from './CustomerFormDialog';
import { CustomerDetailDialog } from './CustomerDetailDialog';
import type { Customer } from '@/shared/api/types';

export function CustomersPage() {
  const [q, setQ] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const { data, isLoading } = useCustomers(q);

  const openNew = () => {
    setEditCustomer(null);
    setFormOpen(true);
  };
  const openEdit = (c: Customer, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditCustomer(c);
    setFormOpen(true);
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900">Kunden</h1>
        <Button onClick={openNew}>
          <Plus className="h-4 w-4" /> Neuer Kunde
        </Button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Kunden suchen…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <Spinner />
      ) : !data || data.items.length === 0 ? (
        <EmptyState
          title={q ? 'Keine Treffer' : 'Noch keine Kunden'}
          hint={q ? 'Andere Suche versuchen.' : 'Legen Sie Ihren ersten Kunden an.'}
        />
      ) : (
        <ul className="space-y-2">
          {data.items.map((c) => (
            <li
              key={c.id}
              onClick={() => setDetailId(c.id)}
              className="flex cursor-pointer items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 hover:border-brand-300 hover:shadow-sm"
            >
              <div className="min-w-0">
                <p className="truncate font-medium text-slate-900">{c.companyName}</p>
                <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-500">
                  {c.email && <span className="truncate">{c.email}</span>}
                  {c._count && (
                    <>
                      <span className="inline-flex items-center gap-1">
                        <Building2 className="h-3 w-3" />
                        {c._count.objects}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <ClipboardList className="h-3 w-3" />
                        {c._count.projects}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={(e) => openEdit(c, e)}
                className="shrink-0 rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <Pencil className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {data && <p className="mt-3 text-xs text-slate-400">{data.total} Kunden</p>}

      <CustomerFormDialog open={formOpen} onClose={() => setFormOpen(false)} customer={editCustomer} />
      <CustomerDetailDialog customerId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
